import { Link, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Trophy, Lock, Flame, Loader2 } from "lucide-react";

import { GET_STUDENT_ACHIEVEMENTS } from "@/graphql/queries/students";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/lib/auth/AuthContext";
import { cn } from "@/lib/utils";

export default function StudentAchievementsPage() {
  // A grown-up arrives with the learner in the URL; a learner arrives at
  // /profile/achievements, where the only possible learner is themselves.
  const { id: idFromUrl } = useParams<{ id: string }>();
  const { role, student: me } = useAuth();
  const id = idFromUrl ?? me?.id;
  const backTo = role === "student" ? "/profile" : `/students/${id}`;

  const { data, loading } = useQuery(GET_STUDENT_ACHIEVEMENTS, {
    variables: { id },
    skip: !id,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const result = data?.studentAchievements;
  if (!result) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Mokinys nerastas</p>
        <Button asChild className="mt-4">
          <Link to={role === "student" ? "/" : "/students"}>
            {role === "student" ? "Į pradžią" : "Grįžti į sąrašą"}
          </Link>
        </Button>
      </div>
    );
  }

  const { student, level, achievements, totalAchievements, unlockedCount } = result;
  const progressPercent = totalAchievements > 0 ? (unlockedCount / totalAchievements) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={backTo}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Pasiekimai</h1>
          <p className="text-muted-foreground">{student.name}</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-yellow-500/10" />
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Lygis</CardTitle>
            <span className="text-2xl">{level?.icon}</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{level?.name}</div>
            <p className="text-xs text-muted-foreground">Lygis {level?.level}</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Taškai</CardTitle>
            <span className="text-2xl">💎</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.totalPoints?.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10" />
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pasiekimai</CardTitle>
            <Trophy className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {unlockedCount} / {totalAchievements}
            </div>
            <div className="mt-2 space-y-1">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {progressPercent.toFixed(0)}% atrakinta
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Streak Info */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          <div>
            <CardTitle className="text-lg">Mokymosi serija</CardTitle>
            <CardDescription>Kiek dienų iš eilės mokeisi</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-8">
            <div>
              <p className="text-3xl font-bold text-orange-500">{student.currentStreak}</p>
              <p className="text-sm text-muted-foreground">Dabartinė serija</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-amber-500">{student.longestStreak}</p>
              <p className="text-sm text-muted-foreground">Rekordas</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Achievements Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Visi pasiekimai</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {achievements?.map((achievement: any) => (
              <div
                key={achievement.id}
                className={cn(
                  "relative flex items-start gap-3 rounded-lg border p-4 transition-all",
                  achievement.isUnlocked
                    ? "border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 dark:border-amber-800 dark:from-amber-950/30 dark:to-yellow-950/30"
                    : "border-border bg-muted/30 opacity-60"
                )}
              >
                <div className={cn(
                  "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xl",
                  achievement.isUnlocked
                    ? "bg-gradient-to-br from-amber-100 to-yellow-100"
                    : "bg-muted"
                )}>
                  {achievement.isUnlocked ? achievement.icon : <Lock className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={cn("text-sm font-medium leading-tight", !achievement.isUnlocked && "text-muted-foreground")}>
                    {achievement.name}
                  </h4>
                  <p className="text-xs text-muted-foreground">{achievement.description}</p>
                  <span className={cn("text-xs font-medium", achievement.isUnlocked ? "text-amber-600" : "text-muted-foreground")}>
                    +{achievement.points} taškų
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
